import React from "react";
import { AxiosResponse } from "axios";
import { TFunction } from "react-i18next";
import { ItemResponse, ListResponse } from "go-core/api/types";
import { MessageEventApi, MessageEventResourceApi, MessageResourceType } from "./types";

export interface MessageEventContextProps {
	all: (
		params?: Record<string, any>,
		options?: Record<string, any>
	) => Promise<AxiosResponse<ListResponse<MessageEventApi>>>;
	get: (id: string, params?: Record<string, any>) => Promise<AxiosResponse<ItemResponse<MessageEventApi>>>;
	getTypeOptions: () => Promise<string[]>;
}

export class MessageEventService {
	_resources: MessageResourceType[];
	_api: MessageEventContextProps;
	_typeTranslationPrefix: string;
	_t: TFunction;
	_sourceAdditionalOptions?: Record<string, string>;

	constructor(
		api: MessageEventContextProps,
		resources: MessageResourceType[],
		typeTranslationPrefix: string,
		t: TFunction,
		sourceAdditionalOptions?: Record<string, string>
	) {
		this._api = api;
		this._resources = resources;
		this._typeTranslationPrefix = typeTranslationPrefix;
		this._t = t;
		this._sourceAdditionalOptions = sourceAdditionalOptions;
	}

	api(): MessageEventContextProps {
		return this._api;
	}

	resources(): MessageResourceType[] {
		return this._resources;
	}

	getResourceUrl(id: string, entityId: string | number, resources?: MessageEventResourceApi[]): string | undefined {
		const type = this._resources.find((x) => x.id === id);
		if (!type?.link) return undefined;

		let newEntityId = entityId;

		if (resources && type.customEntityIdFromResource) {
			const resourceToGetIdFrom = resources.find(
				(resource) => resource.resource_type === type.customEntityIdFromResource
			);
			newEntityId = resourceToGetIdFrom?.resource_id ?? newEntityId;
		}

		return `${type.link.startsWith("/") ? "" : "/"}${type.link}${type.link.endsWith("/") ? "" : "/"}${newEntityId}${
			type.linkAdditionalPath ?? ""
		}`;
	}

	getResourceTitle(id: string): string | undefined {
		const type = this._resources.filter((x) => x.id === id)[0];
		if (!type) return undefined;
		return type.title;
	}

	getMessageEventTypeTitle(messageEventType: string): string {
		const translation = this._t(`${this._typeTranslationPrefix}.${messageEventType.toUpperCase()}`);
		return translation.includes(".") ? messageEventType : translation;
	}

	getSourceAdditionalOptions() {
		return this._sourceAdditionalOptions;
	}
}

export const MessageEventContext = React.createContext<MessageEventService>({} as MessageEventService);
